import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DynamicIcon } from '../components/icons';

// ── Guide data ────────────────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: 'intro', icon: 'BookOpen', label: 'Introduction',
    content: [
      { type: 'p', text: 'The AIDAS Platform (APFv2) is an enterprise AI/ML and analytics operating system. It enables organizations to:' },
      { type: 'bullets', items: ['Build and run AI/ML and data workflows using a visual Flow Editor', 'Create and share interactive dashboards and business applications', 'Query data using natural language (AI Insight)', 'Manage multi-tenant deployments across partners and customers', 'Connect to external databases and automate pipelines with a Scheduler', 'Monitor activity through audit logs, alert rules, and support tickets'] },
      { type: 'table', headers: ['User Group', 'Description'], rows: [['Platform Admins', 'Full access to all system features, tenants, partners, plans'], ['Partner Admins', 'Manage their assigned tenants and users'], ['Tenant Users', 'Access assigned apps, dashboards, flows, and AI tools']] },
    ]
  },
  {
    id: 'start', icon: 'LogIn', label: 'Getting Started',
    content: [
      { type: 'h3', text: 'Logging In' },
      { type: 'steps', items: ['Navigate to your platform URL in a browser.', 'Enter your email and password on the login screen.', 'Click Sign In.'] },
      { type: 'callout', variant: 'important', text: 'If your password has expired, you will be redirected to the Password Change screen immediately. Platform access is blocked until the password is updated.' },
      { type: 'h3', text: 'Resetting a Password' },
      { type: 'bullets', items: ['Click Forgot Password on the login page.', 'Enter your registered email address.', 'Follow the link sent to your inbox to set a new password.'] },
    ]
  },
  {
    id: 'nav', icon: 'LayoutDashboard', label: 'Navigation',
    content: [
      { type: 'h3', text: 'Sidebar' },
      { type: 'table', headers: ['Section', 'Description'], rows: [['Home', 'Landing dashboard and Execution Status'], ['AIDAS Apps', 'System-assigned business app bundles (tenant view)'], ['Published Apps', 'Organization-wide shared dashboards and flows'], ['My Apps', 'Your personal workspace apps'], ['Creator Studio', 'Flows, AI Insight, Dashboards, Business Apps'], ['Data', 'Data Sources, Data Models, Scheduler, Alert Rules'], ['Knowledge Base', 'Tenant-scoped semantic domain definitions for AI Insights (Tenant Admin only)'], ['Administration', 'Partners, Tenants, Users, Roles, Plans, Credits, Settings']] },
      { type: 'h3', text: 'Top Bar' },
      { type: 'bullets', items: ['Bell icon — opens the Notification Center (alert triggers, system events)', 'User avatar — opens profile menu (My Profile, Appearance, Sign Out)'] },
    ]
  },
  {
    id: 'flows', icon: 'Workflow', label: 'Flows (Workflow Studio)',
    content: [
      { type: 'p', text: 'Flows is the visual drag-and-drop engine for building AI, ML, ETL, and automation pipelines. Path: /flows' },
      { type: 'h3', text: 'Flow Editor Canvas' },
      { type: 'table', headers: ['Area', 'Purpose'], rows: [['Node Panel (left)', 'Library of available nodes'], ['Canvas (center)', 'Drag, drop, and connect nodes'], ['Config Panel (right)', 'Configure the selected node']] },
      { type: 'h3', text: 'Node Categories' },
      { type: 'bullets', items: ['Data Nodes — File Source, File Object, Database Reader, API Connector', 'Transform Nodes — Filter, Join, Aggregate, Formula', 'AI/ML Nodes — LLM Prompt, AI Insight, AI Agent, ML Model', 'Output Nodes — Dashboard Writer, Email, Webhook, Database Writer'] },
      { type: 'h3', text: 'Running a Flow' },
      { type: 'steps', items: ['Click ▶ Run in the top toolbar.', 'Monitor progress in the Execution Log panel (bottom or via /logs).', 'On completion, output data is available to downstream nodes or published dashboards.'] },
      { type: 'h3', text: 'Publishing a Flow' },
      { type: 'steps', items: ['Click Publish in the toolbar.', 'Choose a Content Type (AI/ML Prediction, GenAI App, Workflow App, ETL).', 'Set the sharing scope (private, organization-wide, or specific tenants).', 'Confirm to make the flow available in the Business Apps catalogue.'] },
    ]
  },
  {
    id: 'ai', icon: 'BrainCircuit', label: 'AI Insight',
    content: [
      { type: 'p', text: 'AI Insight is the conversational analytics engine inside AIDAS. Ask questions in plain English and get instant data answers, charts, and written summaries — no SQL required. Path: /ai-insight' },
      { type: 'callout', variant: 'note', text: 'Access is controlled by the ai_insights feature flag. If AI Insight does not appear in your sidebar, contact your Tenant Admin or Platform Admin to confirm the feature is enabled.' },
      { type: 'h3', text: 'Starting a Conversation' },
      { type: 'steps', items: ['Click AI Insight in the sidebar. The screen is split: session history on the left, active chat on the right.', 'Click the + icon at the top of the sidebar to start a new chat session.', 'Click the data model pill (cable icon, bottom-left of input bar) and select the dataset you want to query. The input bar is locked until a data model is chosen.', 'Type your question and press Enter — or click any suggested insight chip above the input bar.'] },
      { type: 'h3', text: 'Suggested Insights' },
      { type: 'p', text: 'As soon as you select a data model, AIDAS analyses its schema and shows ready-to-ask suggestion chips above the input bar. These are generated from column structure — not row data — and give you a smart starting point. Click any chip to send it instantly, scroll left/right to browse, or dismiss the strip with the × button.' },
      { type: 'h3', text: 'Switching the AI Model' },
      { type: 'p', text: 'Click the model pill (✦ sparkle icon, next to the data model pill) to choose the AI engine for your question.' },
      { type: 'table', headers: ['Model', 'Best For'], rows: [['Default', 'Balanced — uses the tenant or platform default'], ['GPT-4o Mini', 'Fast responses, lowest cost'], ['GPT-4o', 'High accuracy, deeper analysis'], ['GPT-5 Mini', 'Latest compact model, good reasoning'], ['GPT-5', 'Flagship model, maximum intelligence']] },
      { type: 'callout', variant: 'note', text: 'Tenant Admins can restrict which models are available to users from Administration → AI Settings. Platform Admins manage API keys and platform defaults from Platform Settings → AI Configuration.' },
      { type: 'h3', text: 'Format Options' },
      { type: 'p', text: 'Click the tune icon (right side of the input bar) to control how the AI structures its response.' },
      { type: 'table', headers: ['Option', 'What it does'], rows: [['Bullet Points', 'Presents insights as structured bullet points'], ['Summary', 'Adds a brief paragraph summary'], ['Compact Numbers', 'Formats large numbers as $1.2M, 3.4K, etc.'], ['Data Metrics', 'AI highlights key stats (max, min, avg) from the data'], ['Similar Follow-ups', 'Suggests related questions at the end of the response']] },
      { type: 'h3', text: 'Reading a Result' },
      { type: 'p', text: 'Each AI response is a card with up to three collapsible sections:' },
      { type: 'bullets', items: ['DATA (cyan) — Chart or data table. Use the Chart / Table toggle to switch views. Key metric chips (Max, Min, Avg) appear above. Click Pin to save to the Report Builder.', 'INSIGHTS (purple) — AI-generated narrative. Positive findings highlighted green, negative red, key numbers amber. Click the pencil icon to edit before pinning. Click Pin to save the narrative.', 'Follow-up chips — If Similar Follow-ups is enabled, clickable question chips appear at the bottom of the result for instant drill-down.'] },
      { type: 'h3', text: 'Clarification' },
      { type: 'p', text: 'If your question is ambiguous, the AI presents a Clarification Panel instead of guessing. Click the card that matches your intent, or type a more specific description in the text box, then resubmit.' },
      { type: 'h3', text: 'Semantic Explanations' },
      { type: 'p', text: 'Ask the AI to explain a business term without running a data query — e.g. "What is Net Revenue Retention?". If your Knowledge Base has a custom definition, the AI uses it. If the question is outside the analytics domain, the AI answers briefly and shows an Out-of-Scope warning banner.' },
      { type: 'h3', text: 'Chart Types Supported' },
      { type: 'table', headers: ['Category', 'Chart Types'], rows: [['Trends', 'Line Chart, Area Chart, Combo Chart (bars + lines), Sparkline'], ['Comparisons', 'Bar Chart, Grouped Bar, Stacked Bar, Waterfall Chart'], ['Composition', 'Pie Chart, Donut Chart, Treemap'], ['Performance', 'KPI Card'], ['Relations', 'Scatter Chart, Bubble Chart'], ['Flow', 'Funnel Chart'], ['Detail', 'Heatmap, Data Table']] },
      { type: 'callout', variant: 'tip', text: 'Specify the chart type in your question — e.g. "Show denial reasons as a donut chart". If not specified, the AI picks the best chart for the data shape returned.' },
      { type: 'h3', text: 'Session History & Management' },
      { type: 'p', text: 'Every conversation is auto-saved as a named session visible only to you.' },
      { type: 'table', headers: ['Action', 'How'], rows: [['Rename a session', 'Click the pencil icon on hover, or double-click the session title'], ['Delete a session', 'Click the trash icon on hover (permanent)'], ['Search sessions', 'Use the search bar at the top of the sidebar']] },
      { type: 'h3', text: 'Report Builder' },
      { type: 'steps', items: ['Pin charts (DATA section) and narratives (INSIGHTS section) from any result card using the Pin icon.', 'Open the Report Builder panel from the right side of the AI Insight screen. Tick checkboxes next to items to include, drag to reorder, rename with the pencil icon, or remove with the trash icon.', 'Click Generate Report → to open the full Report Canvas.', 'On the canvas: drag sections to reorder, double-click titles to rename, use the rich-text toolbar to edit narratives, drag the resize handle to resize charts, and set Half-width or Full-width layout per section.', 'Click Enhance with AI in the canvas toolbar to rewrite all narrative sections with optional custom instructions. Charts and data are preserved.', 'Click Export and choose PDF, Excel (.xlsx), or Chartifact (self-contained HTML web page).'] },
      { type: 'callout', variant: 'tip', text: 'Canvas edits are auto-saved to your browser local storage — you will not lose progress if you navigate away.' },
    ]
  },
  {
    id: 'kb', icon: 'Library', label: 'Knowledge Base',
    content: [
      { type: 'p', text: 'The Knowledge Base is the intelligence layer that teaches the AI your business terminology, metric definitions, and mandatory rules — so AI Insight answers are accurate to your organisation, not just generic. Path: /knowledge-base' },
      { type: 'callout', variant: 'important', text: 'Knowledge Base management is restricted to Tenant Admins and Platform Admins. Regular users benefit from it indirectly when AI Insight uses the configured domains to answer their questions.' },
      { type: 'h3', text: 'Core Concepts' },
      { type: 'table', headers: ['Concept', 'What it means'], rows: [['Domain', 'A named group of related metrics and rules (e.g. "Healthcare Terminology", "Finance KPIs")'], ['Metric', 'A business calculation the AI must always use — e.g. Net Margin = (Revenue − COGS) / Revenue × 100'], ['Rule', 'A mandatory instruction — e.g. "Always exclude accounts with status = Test from every report"'], ['Binding', 'Linking a domain to a Data Model so AI Insight queries against that model use your definitions automatically']] },
      { type: 'h3', text: 'Viewing Existing Domains' },
      { type: 'p', text: 'The Knowledge Base home screen shows all domains as cards. Each card displays the domain name, description, number of Metrics and Rules, and a status badge (Active / Inactive).' },
      { type: 'h3', text: 'Creating a Domain Manually' },
      { type: 'steps', items: ['Click New Domain (top right of screen).', 'Enter a Name (e.g. "Healthcare Terminology") and a Description.', 'Click Create. The domain card appears on the home screen.', 'Click the card to open it and start adding Metrics and Rules.'] },
      { type: 'h3', text: 'Generate a Domain with AI' },
      { type: 'steps', items: ['Click Generate using AI (top right, next to New Domain).', 'In the dialog, type a description of your domain — at least 10 characters. Example: "Healthcare claims processing with providers, patients, payers, and reimbursement amounts."', 'Click Generate. The AI creates a domain with a starter set of metrics and rules.', 'Open the domain to review and edit before activating.'] },
      { type: 'callout', variant: 'tip', text: 'Treat AI-generated domains as first drafts. Always review metrics and rules to ensure they match your exact business definitions.' },
      { type: 'h3', text: 'Adding Metrics (Metrics Tab)' },
      { type: 'table', headers: ['Action', 'How'], rows: [['Add a Metric', 'Click Add Metric. Enter a name, description, and formula/definition.'], ['Edit a Metric', 'Click the pencil icon on the metric row.'], ['Delete a Metric', 'Click the trash icon. A confirmation prompt appears.']] },
      { type: 'callout', variant: 'tip', text: 'Once defined here, the AI always uses your exact formula — it will never guess or hallucinate the calculation, even if a textbook definition differs.' },
      { type: 'h3', text: 'Adding Rules (Rules Tab)' },
      { type: 'table', headers: ['Action', 'How'], rows: [['Add a Rule', 'Click Add Rule. Enter a name, description, rule text, and Priority (Low / Medium / High).'], ['Edit a Rule', 'Click the pencil icon.'], ['Delete a Rule', 'Click the trash icon and confirm.']] },
      { type: 'p', text: 'Priority controls how strictly the AI enforces the rule: High = always enforced even if a user question contradicts it. Medium = applied unless the user explicitly overrides. Low = used as a preference, not a hard constraint.' },
      { type: 'h3', text: 'Uploading a Document' },
      { type: 'p', text: 'Upload an existing data dictionary, policy document, or glossary file — the AI extracts metrics and rules automatically. Supported formats: PDF, DOCX, XLSX, CSV.' },
      { type: 'steps', items: ['Open a domain and click Upload Document.', 'Select your file and click Upload & Preview.', 'Review the extracted Entities, Metrics, and Rules. Edit any field inline or remove items you do not want. Duplicates are flagged and skipped automatically.', 'Click Confirm & Add to Domain to save the approved items.'] },
      { type: 'h3', text: 'Bulk Import via Excel Templates' },
      { type: 'steps', items: ['Inside the Upload Document dialog, find the Template section on the left.', 'Click Metrics Template or Rules Template to download the Excel file.', 'Fill in the template following the column headers provided.', 'Upload the completed file back through the same dialog and confirm the import.'] },
      { type: 'callout', variant: 'tip', text: 'Excel templates are the fastest way to migrate an existing internal glossary or data dictionary into the Knowledge Base.' },
      { type: 'h3', text: 'Editing & Deleting Domains' },
      { type: 'bullets', items: ['Edit a Domain — hover over the domain card and click the pencil icon to change name, description, or status.', 'Delete a Domain — hover and click the trash icon. If the domain is mapped to any Data Model, a warning lists the affected models before you confirm.'] },
      { type: 'callout', variant: 'caution', text: 'Deleting an active, mapped domain immediately removes its context from AI Insight for all data models it was bound to. This will affect answer quality until a new domain is bound.' },
      { type: 'h3', text: 'Binding a Domain to a Data Model' },
      { type: 'p', text: 'Binding activates your Knowledge Base for AI Insight queries. Until bound, the AI does not use your domain definitions — even if they are fully configured.' },
      { type: 'steps', items: ['Navigate to Data Models in the sidebar.', 'Open the target Data Model settings.', 'In the Knowledge Base section, select your domain from the dropdown.', 'Click Bind Domain.'] },
      { type: 'p', text: 'From that point on, every AI Insight query against that Data Model will automatically use your metrics, rules, and terminology.' },
      { type: 'callout', variant: 'tip', text: 'Think of binding as giving the AI a company handbook to refer to before answering anything. Accuracy improves significantly once a domain is actively bound to a data model.' },
    ]
  },
  {
    id: 'dashboards', icon: 'BarChart2', label: 'Dashboards',
    content: [
      { type: 'p', text: 'The Dashboard Studio lets you create and share interactive analytics dashboards. Path: /dashboards' },
      { type: 'h3', text: 'Widget Types' },
      { type: 'table', headers: ['Widget', 'Description'], rows: [['Bar / Line / Pie Chart', 'Visualization bound to a data model query'], ['KPI Card', 'Single metric with optional trend'], ['Table', 'Paginated grid of rows'], ['Text / Markdown', 'Freeform annotations'], ['Filter Bar', 'User-controlled filter applied to all widgets']] },
      { type: 'h3', text: 'Sharing' },
      { type: 'bullets', items: ['Private — only you can view', 'Organization-Wide — all users in your tenant', 'Embed — generate an embed URL for external portals'] },
    ]
  },
  {
    id: 'apps', icon: 'LayoutGrid', label: 'Business Apps',
    content: [
      { type: 'p', text: 'Business Apps is the marketplace where published content is bundled into application packages and assigned to tenants. Path: /business-apps' },
      { type: 'h3', text: 'Content Types' },
      { type: 'table', headers: ['Type', 'Description'], rows: [['AI/ML Predictions', 'ML model inference runner'], ['GenAI App', 'LLM/GenAI powered application'], ['Dashboard', 'Interactive analytics dashboard'], ['Workflow App', 'Automated business process']] },
      { type: 'h3', text: 'Creating an App Bundle' },
      { type: 'steps', items: ['Click + New App Bundle.', 'Give it a Name and optional Icon.', 'Add content items (dashboards, flows, ML models) from the catalogue.', 'Click Save.'] },
    ]
  },
  {
    id: 'data', icon: 'Database', label: 'Data Sources & Models',
    content: [
      { type: 'h3', text: 'Data Sources (/datasources)' },
      { type: 'steps', items: ['Click + New Connection.', 'Select a connector type: PostgreSQL, MySQL, MSSQL, REST API, S3, or others.', 'Fill in host, port, database name, credentials.', 'Click Test Connection to verify.', 'Click Save.'] },
      { type: 'callout', variant: 'tip', text: 'For on-premise databases behind a firewall, use a Relay Agent. The connection is routed securely through the agent.' },
      { type: 'h3', text: 'Data Models (/datamodels)' },
      { type: 'p', text: 'Data Models define the semantic layer — the business-friendly representation of raw database tables, joins, and calculated fields.' },
      { type: 'steps', items: ['Click + New Data Model.', 'Select a Data Source.', 'Add tables and define joins using the visual canvas.', 'Add calculated fields and filters.', 'Click Publish to make the model available to dashboards and AI Insight.'] },
    ]
  },
  {
    id: 'scheduler', icon: 'Clock', label: 'Scheduler & Alerts',
    content: [
      { type: 'h3', text: 'Scheduler (/automations)' },
      { type: 'steps', items: ['Click + New Schedule.', 'Select the Flow to automate.', 'Set the trigger type: Cron, Interval, or One-time.', 'Set optional timeout and retry settings.', 'Click Save & Activate.'] },
      { type: 'table', headers: ['Action', 'Description'], rows: [['Pause', 'Temporarily disable without deleting'], ['Resume', 'Re-activate a paused schedule'], ['Run Now', 'Trigger an immediate manual run'], ['Delete', 'Permanently remove the schedule']] },
      { type: 'h3', text: 'Alert Rules (/alerts)' },
      { type: 'steps', items: ['Click + New Alert Rule.', 'Select a Data Source and metric.', 'Define a condition (e.g., value > 1000).', 'Set notification channels: Email, Webhook, or in-app.', 'Set evaluation frequency.', 'Click Save & Enable.'] },
    ]
  },
  {
    id: 'admin', icon: 'ShieldCheck', label: 'Administration',
    content: [
      { type: 'h3', text: 'Users & Roles' },
      { type: 'bullets', items: ['Add User: Click + Invite User, enter email, select role.', 'Edit User: Click a user row to change name, role, or status.', 'Deactivate User: Toggle the Active switch to revoke access without deleting.', 'Reset Password: Click the action menu → Send Password Reset.'] },
      { type: 'h3', text: 'Built-in Roles' },
      { type: 'table', headers: ['Role', 'Name', 'Scope'], rows: [['r1', 'Platform Super Admin', 'Full platform'], ['r2', 'Platform Admin', 'Full platform'], ['r3', 'Partner Admin', 'Partner + assigned tenants'], ['r4', 'Tenant Admin', 'Single tenant'], ['r6', 'Tenant Creator', 'Create content within tenant'], ['r7', 'Tenant Viewer', 'Read-only access to apps']] },
      { type: 'h3', text: 'Tenant Management' },
      { type: 'steps', items: ['Click + New Tenant.', 'Fill in Organization Name, Domain, and Plan.', 'Assign to a Partner (optional).', 'Click Create.'] },
    ]
  },
  {
    id: 'relay', icon: 'Server', label: 'Relay Agents',
    content: [
      { type: 'p', text: 'Relay Agents are lightweight software agents installed on-premises that allow the AIDAS platform to securely reach databases inside private networks without opening firewall ports.' },
      { type: 'callout', variant: 'note', text: 'The agent initiates an outbound WebSocket connection to the platform. All data flows through this secure tunnel: AIDAS Platform ──── HTTPS ────► Relay Agent ──── Internal Network ────► Database' },
      { type: 'h3', text: 'Installing a Relay Agent' },
      { type: 'steps', items: ['Go to Settings → Relay Agents.', 'Click + New Agent.', 'Give the agent a Name and optional Description.', 'Click Generate Token — copy the registration token.', 'On your on-premise server run: pip install aidas-relay-agent && aidas-relay-agent register --token <TOKEN> --server https://your-platform-url && aidas-relay-agent start'] },
      { type: 'table', headers: ['Status', 'Meaning'], rows: [['ONLINE', 'Agent is connected and healthy'], ['IDLE', 'Agent missed heartbeat (>2 min); will reconnect'], ['PENDING', 'Token issued, agent not yet connected'], ['OFFLINE', 'Agent manually deregistered']] },
    ]
  },
  {
    id: 'shortcuts', icon: 'Keyboard', label: 'Shortcuts & Glossary',
    content: [
      { type: 'h3', text: 'Keyboard Shortcuts' },
      { type: 'table', headers: ['Shortcut', 'Action'], rows: [['Ctrl + Enter', 'Run SQL / Flow'], ['Ctrl + S', 'Save current work'], ['Escape', 'Close modal / panel']] },
      { type: 'h3', text: 'Glossary' },
      { type: 'table', headers: ['Term', 'Definition'], rows: [['Flow', 'A visual pipeline of connected processing nodes'], ['Node', 'A single processing step within a flow'], ['Data Model', 'A semantic layer mapping raw tables to business concepts'], ['App Bundle', 'A packaged collection of dashboards and flows'], ['Tenant', 'An organization with its own isolated workspace'], ['Partner', 'A reseller or implementor managing multiple tenants'], ['Relay Agent', 'On-premise software bridging private networks to the platform'], ['NLQ', 'Natural Language Query — asking questions in plain English'], ['Credit', 'A unit of compute resource consumption']] },
    ]
  },
  {
    id: 'nodes', icon: 'Box', label: 'Node Reference Guide', iframe: '/node_user_guide.html',
    content: []
  },
];

// ── Block renderers ───────────────────────────────────────────────────────────
const CALLOUT_STYLES = {
  tip:       { bg: 'bg-green-50 dark:bg-green-900/20',  border: 'border-green-300 dark:border-green-700',  icon: 'Lightbulb',   text: 'text-green-800 dark:text-green-300',  label: 'Tip' },
  important: { bg: 'bg-amber-50 dark:bg-amber-900/20',  border: 'border-amber-300 dark:border-amber-700',  icon: 'AlertTriangle', text: 'text-amber-800 dark:text-amber-300', label: 'Important' },
  note:      { bg: 'bg-blue-50 dark:bg-blue-900/20',    border: 'border-blue-300 dark:border-blue-700',    icon: 'Info',        text: 'text-blue-800 dark:text-blue-300',    label: 'Note' },
  caution:   { bg: 'bg-red-50 dark:bg-red-900/20',      border: 'border-red-300 dark:border-red-700',      icon: 'ShieldAlert', text: 'text-red-800 dark:text-red-300',      label: 'Caution' },
};

const Block = ({ block }) => {
  switch (block.type) {
    case 'p':
      return <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{block.text}</p>;
    case 'h3':
      return <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mt-5 mb-2 flex items-center gap-2"><span className="w-1 h-4 bg-indigo-500 rounded-full inline-block" />{block.text}</h3>;
    case 'bullets':
      return (
        <ul className="space-y-1.5 ml-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <ol className="space-y-2 ml-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
              <span className="shrink-0 w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
              {item}
            </li>
          ))}
        </ol>
      );
    case 'table':
      return (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 my-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800">
                {block.headers.map((h, i) => (
                  <th key={i} className="px-4 py-2.5 text-left font-semibold text-gray-700 dark:text-gray-200 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {block.rows.map((row, ri) => (
                <tr key={ri} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-4 py-2.5 text-gray-600 dark:text-gray-300 ${ci === 0 ? 'font-medium text-gray-800 dark:text-gray-200' : ''}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'callout': {
      const s = CALLOUT_STYLES[block.variant] || CALLOUT_STYLES.note;
      return (
        <div className={`flex gap-3 p-4 rounded-lg border ${s.bg} ${s.border} my-2`}>
          <DynamicIcon name={s.icon} className={`w-4 h-4 mt-0.5 shrink-0 ${s.text}`} />
          <div className={`text-sm ${s.text}`}>
            <span className="font-semibold">{s.label}: </span>{block.text}
          </div>
        </div>
      );
    }
    default: return null;
  }
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const UserGuidePage = ({ onClose }) => {
  const [activeId, setActiveId] = useState('intro');
  const [search, setSearch]     = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const active = SECTIONS.find(s => s.id === activeId) || SECTIONS[0];

  const filtered = search.trim()
    ? SECTIONS.filter(s => s.label.toLowerCase().includes(search.toLowerCase()))
    : SECTIONS;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50 dark:bg-gray-950">

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between px-5 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(v => !v)}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors"
          >
            <DynamicIcon name="PanelLeft" className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-indigo-600 rounded-lg">
              <DynamicIcon name="BookOpen" className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-gray-900 dark:text-white text-sm">AIDAS Platform — User Guide</span>
            <span className="text-xs px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full font-medium">v2.0</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          title="Close"
        >
          <DynamicIcon name="X" className="w-5 h-5" />
        </button>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 min-h-0">

        {/* Sidebar */}
        <AnimatePresence initial={false}>
          {sidebarOpen && (
            <motion.aside
              key="sidebar"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 260, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 30 }}
              className="shrink-0 overflow-hidden bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col"
            >
              {/* Search */}
              <div className="p-3 border-b border-gray-100 dark:border-gray-800">
                <div className="relative">
                  <DynamicIcon name="Search" className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search sections…"
                    className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  />
                </div>
              </div>

              {/* Nav items */}
              <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
                {filtered.map((s, idx) => (
                  <button
                    key={s.id}
                    onClick={() => { setActiveId(s.id); setSearch(''); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                      activeId === s.id
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    <DynamicIcon name={s.icon} className="w-4 h-4 shrink-0" />
                    <span className="truncate">{s.label}</span>
                  </button>
                ))}
              </nav>

              {/* Footer */}
              <div className="p-3 border-t border-gray-100 dark:border-gray-800 text-center text-xs text-gray-400">
                © AIDAS Platform — Confidential
              </div>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Content */}
        <main className="flex-1 flex flex-col min-h-0">
          {/* Hero banner — hidden for iframe sections */}
          {!active.iframe && (
          <div className="relative bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800 px-8 py-8 overflow-hidden shrink-0">
            <div className="absolute inset-0 opacity-10 pointer-events-none"
              style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, white 0%, transparent 60%)' }} />
            <div className="relative z-10 flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                <DynamicIcon name={active.icon} className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-indigo-200 text-xs uppercase tracking-widest font-medium mb-0.5">
                  {SECTIONS.findIndex(s => s.id === activeId) + 1} of {SECTIONS.length}
                </p>
                <h1 className="text-2xl font-bold text-white">{active.label}</h1>
              </div>
            </div>

            {/* Prev / Next */}
            {(() => {
              const idx = SECTIONS.findIndex(s => s.id === activeId);
              const hasPrev = idx > 0;
              const hasNext = idx < SECTIONS.length - 1;
              const prevSection = hasPrev ? SECTIONS[idx - 1] : null;
              const nextSection = hasNext ? SECTIONS[idx + 1] : null;
              return (
                <div className="absolute right-6 top-1/2 -translate-y-1/2 flex gap-2 z-10">
                  <button
                    onClick={() => hasPrev && setActiveId(prevSection.id)}
                    disabled={!hasPrev}
                    title={hasPrev ? `Previous: ${prevSection.label}` : ''}
                    className={`p-2 rounded-lg text-white transition-colors ${hasPrev ? 'bg-white/10 hover:bg-white/20 cursor-pointer' : 'bg-white/5 opacity-30 cursor-not-allowed'}`}
                  >
                    <DynamicIcon name="ChevronLeft" className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => hasNext && setActiveId(nextSection.id)}
                    disabled={!hasNext}
                    title={hasNext ? `Next: ${nextSection.label}` : ''}
                    className={`p-2 rounded-lg text-white transition-colors ${hasNext ? 'bg-white/10 hover:bg-white/20 cursor-pointer' : 'bg-white/5 opacity-30 cursor-not-allowed'}`}
                  >
                    <DynamicIcon name="ChevronRight" className="w-4 h-4" />
                  </button>
                </div>
              );
            })()}
          </div>
          )}


          {/* iframe section */}
          {active.iframe && (
            <iframe
              key={activeId}
              src={active.iframe}
              className="flex-1 w-full border-0"
              title={active.label}
            />
          )}

          {/* Section body */}
          {!active.iframe && (
          <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeId}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="max-w-3xl mx-auto px-8 py-8 space-y-4"
            >
              {active.content.map((block, i) => <Block key={i} block={block} />)}

              {/* Next section card */}
              {SECTIONS.findIndex(s => s.id === activeId) < SECTIONS.length - 1 && (() => {
                const next = SECTIONS[SECTIONS.findIndex(s => s.id === activeId) + 1];
                return (
                  <button
                    onClick={() => setActiveId(next.id)}
                    className="mt-8 w-full flex items-center justify-between p-4 rounded-xl border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <DynamicIcon name={next.icon} className="w-5 h-5 text-indigo-500" />
                      <div className="text-left">
                        <p className="text-xs text-indigo-400 mb-0.5">Next Section</p>
                        <p className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">{next.label}</p>
                      </div>
                    </div>
                    <DynamicIcon name="ArrowRight" className="w-4 h-4 text-indigo-400 group-hover:translate-x-1 transition-transform" />
                  </button>
                );
              })()}
            </motion.div>
          </AnimatePresence>
          </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default UserGuidePage;
